import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

/** One org-settings string list, read through the same cache key AddOptionInline invalidates. */
function useRegistryValues(slug: string, bodyKey: string): { values: string[]; isLoading: boolean } {
  const query = useQuery<string[] | { values?: string[] } & Record<string, unknown>>({
    queryKey: ['org-settings', slug],
    queryFn: ({ signal }) => api.get(`/api/org-settings/${slug}`, signal),
    staleTime: 5 * 60 * 1000,
  });
  const data = query.data;
  const values = useMemo(() => {
    const raw = Array.isArray(data) ? data : ((data?.values ?? data?.[bodyKey] ?? []) as unknown[]);
    return raw.filter((v): v is string => typeof v === 'string');
  }, [data, bodyKey]);
  return { values, isLoading: query.isLoading };
}

/**
 * The org's client-categories list (Org Settings), spec 0011. ONE list feeds the client's
 * categories set (wizard, profile, clients-list filter, CSV validator) and the single
 * Category on each property and parcel, so every surface reads it through this hook and
 * shares one cache entry. The query key matches the one AddOptionInline invalidates, so an
 * option added inline appears everywhere at once.
 */
export function useClientCategories(): { values: string[]; isLoading: boolean } {
  return useRegistryValues('client-categories', 'client_categories');
}

/** The org's client-types list (Org Settings): the values a client's Types set may hold. */
export function useClientTypes(): { values: string[]; isLoading: boolean } {
  return useRegistryValues('client-types', 'client_types');
}
